import React from "react"
import styled, { createGlobalStyle, css } from "styled-components"
import { StructuredText } from "react-datocms"
import colors from "@constants/colors"
import { h6, body, bodyBold } from "@constants/typography"
import MaxWidthContainer from "@components/Layout/MaxWidthContainer"
import MaxWidthContent from "@components/Layout/MaxWidthContent"
import MarkdownIt from "markdown-it"
const Markdown = new MarkdownIt()

const Header = styled.h6<{ center?: boolean }>`
  ${({ center }) => css`
    ${h6}
    margin-bottom: 42px;
    color: ${colors.obsidian};
    text-align: ${center ? "center" : "left"};
  `}
`

const Body = styled.div<{ center?: boolean }>`
  ${({ center }) => css`
    ${body}
    margin-bottom: 42px;
    color: ${colors.slateDark};
    text-align: ${center ? "center" : "left"};

    img {
      max-width: 100%;
      display: block;
      margin: 24px auto;
    }

    strong {
      font-weight: 600;
    }

    em {
      font-style: italic;
    }

    a {
      text-decoration-line: underline;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      ${h6}
      margin-bottom: 42px;
      color: ${colors.obsidian};

      &:not(:first-child) {
        margin-top: 84px;
      }
    }

    p {
      margin-bottom: 24px;
    }

    ul {
      list-style: none;
      padding-left: 0;
      margin-bottom: 24px;

      li:before {
        content: "-";
        display: inline-block;
        margin-right: 0.5em;
      }
    }
  `}
`

const Table = styled.div`
  table {
    width: 100%;
    border: 1px solid ${colors.lightestGray};

    th,
    td {
      ${body}
      padding: 16px 8px;
    }

    th {
      text-align: left;
      ${bodyBold}
    }
  }
`

const GlobalHeadingStyles = createGlobalStyle`
  .richtext h1 { font-size: 2.5rem !important; font-weight: 700; line-height: 1.2; }
  .richtext h2 { font-size: 2rem !important; font-weight: 700; line-height: 1.3; }
  .richtext h3 { font-size: 1.75rem !important; font-weight: 700; line-height: 1.4; }
  .richtext h4 { font-size: 1.5rem !important; font-weight: 700; line-height: 1.5; }
  .richtext h5 { font-size: 1.25rem !important; font-weight: 700; line-height: 1.6; }
  .richtext h6 { font-size: 1rem !important; line-height: 1.6; }
  .richtext table, .richtext table tr td { border: none; }
`

interface ImageRecord {
  __typename: "ImageRecord"
  image: {
    url: string
    alt?: string
  }
}

interface TextBlockProps {
  header?: string
  render?: string
  body: any // for structured text, this is the full object from DatoCMS
  centerHeader?: boolean
  centerBody?: boolean
}

const TextBlock: React.FC<TextBlockProps> = ({
  header,
  render,
  body,
  centerHeader,
  centerBody,
}) => {
  let table_array: any = null
  let processedBody = body

  if (typeof body === "string" && body.includes("-TABLE-")) {
    const TABLE_START = body.indexOf("-TABLE-")
    const TABLE_END = body.indexOf("-END TABLE-")
    const table = body.substring(TABLE_START, TABLE_END)
    processedBody =
      body.substring(0, TABLE_START) +
      body.substring(TABLE_END + 11, body.length)

    const rows = table.split("\n").slice(1, -1)
    table_array = rows.map((row) => row.split(","))
  }

  return (
    <MaxWidthContainer contentStyle={{ justifyContent: "center" }}>
      <GlobalHeadingStyles />
      <MaxWidthContent cols={8}>
        {header && <Header center={centerHeader}>{header}</Header>}

        <Body center={centerBody}>
          <div className={render === "wysiwyg" ? "richtext" : undefined}>
            {render === "wysiwyg" && body?.value ? (
              <StructuredText
                data={body}
                renderInlineRecord={({ record }) => {
                  if (record.__typename === "ImageRecord") {
                    return (
                      <img
                        src={record.image.url}
                        alt={record.image.alt || ""}
                      />
                    )
                  }
                  return null
                }}
                renderBlock={({ record }) => {
                  if (record.__typename === "ImageRecord") {
                    return (
                      <img
                        src={record.image.url}
                        alt={record.image.alt || ""}
                      />
                    )
                  }
                  return null
                }}
                renderNode={{
                  heading: ({ node, children }) => {
                    const level = node.level || 2
                    const Tag = `h${level}` as keyof JSX.IntrinsicElements
                    return <Tag>{children}</Tag>
                  },
                }}
              />
            ) : render === "basic" ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: Markdown.render(processedBody),
                }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: processedBody }} />
            )}
          </div>
        </Body>

        {table_array && (
          <Table>
            <table>
              {table_array.map((row: string[], index: number) => (
                <tr key={index}>
                  {row.map((cell, i) =>
                    index === 0 ? (
                      <th key={i}>{cell}</th>
                    ) : (
                      <td key={i}>{cell}</td>
                    )
                  )}
                </tr>
              ))}
            </table>
          </Table>
        )}
      </MaxWidthContent>
    </MaxWidthContainer>
  )
}

export default TextBlock
